import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileMinus2, Loader2, FileDown } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { downloadPdf } from "@/components/PdfButton";
import { useLang } from "@/lib/i18n";
import { today } from "@/lib/format";
import { Field, Money } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export function CreditNotes({ invoice }) {
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ mode: "full", amount: "", reason: "", issue_date: today() });
  const load = () => api.get("/credit-notes", { params: { invoice_id: invoice.id } }).then((r) => setRows(r.data));
  useEffect(() => { load(); }, [invoice.id]); // eslint-disable-line
  const credited = rows.reduce((s, r) => s + r.total, 0);
  const remaining = Math.round((invoice.total - credited) * 100) / 100;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { invoice_id: invoice.id, issue_date: f.issue_date, reason: f.reason };
      if (f.mode === "partial") body.items = [{ description: `${t("creditNote")} ${invoice.number}`, quantity: 1, unit_price: Number(f.amount), vat_type: invoice.items[0]?.vat_type || "standard" }];
      await api.post("/credit-notes", body);
      toast.success(t("creditNoteCreated"));
      setOpen(false);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <>
      {remaining > 0.009 && <Button variant="outline" data-testid="invoice-credit-note-btn" onClick={() => setOpen(true)}><FileMinus2 className="me-1 h-4 w-4" />{t("createCreditNote")}</Button>}
      {rows.length > 0 && (
        <div data-testid="invoice-credit-notes" className="no-print mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-red-100 bg-red-50/50 px-4 py-2 text-sm">
          <span className="font-semibold text-[#A50E1F]">{t("creditNotes")}:</span>
          {rows.map((r) => <span key={r.id} className="inline-flex items-center gap-1"><Link data-testid={`credit-note-link-${r.number}`} to={`/credit-notes/${r.id}`} className="font-mono text-[#A50E1F] hover:underline">{r.number} (−<Money value={r.total} cur={false} />)</Link><button type="button" data-testid={`credit-note-pdf-${r.number}`} title="PDF" onClick={() => downloadPdf("credit_note", r.id, r.number)} className="rounded p-1 text-slate-400 hover:bg-white hover:text-slate-900"><FileDown className="h-3.5 w-3.5" /></button></span>)}
          <span className="ms-auto text-slate-600">{t("remainingDue")}: <Money value={remaining} className="font-semibold" /></span>
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="credit-note-dialog">
          <DialogHeader><DialogTitle>{t("createCreditNote")} — {invoice.number}</DialogTitle><DialogDescription>{t("cnFullText")}</DialogDescription></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="flex gap-2">
              {["full", "partial"].map((m) => (
                <button type="button" key={m} data-testid={`cn-mode-${m}`} onClick={() => setF({ ...f, mode: m })} className={`rounded-full px-4 py-1.5 text-sm font-semibold ${f.mode === m ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>{t(`cn_${m}`)}</button>
              ))}
            </div>
            {f.mode === "full"
              ? <p className="text-sm text-slate-600">{t("cnFullText")} <Money value={remaining} className="font-semibold" /></p>
              : <Field label={`${t("amount")} (${t("exclVat")})`}><Input data-testid="cn-amount-input" type="number" min="0.01" step="0.01" required value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} /></Field>}
            <Field label={t("date")}><Input data-testid="cn-date-input" type="date" value={f.issue_date} onChange={(e) => setF({ ...f, issue_date: e.target.value })} required /></Field>
            <Field label={t("reason")}><Input data-testid="cn-reason-input" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} placeholder={t("reasonPlaceholder")} /></Field>
            <Button data-testid="cn-submit-btn" disabled={busy} className="w-full bg-[#CE1126] hover:bg-[#A50E1F]">{busy && <Loader2 className="me-2 h-4 w-4 animate-spin" />}{t("issueCreditNote")}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
